import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  timeTrackingService,
  TimeEntry,
  CreateTimeEntryInput,
  UpdateTimeEntryInput,
} from "../services/timeTracking";
import { useTimeEntriesCache } from "./useTimeEntriesCache";
import { getEffectiveTimezone } from "../utils/timezoneOptions";

export function useTimeTracking(
  isAuthenticated: boolean = false
) {
  // Get cache methods for optimistic updates
  const { addEntryToCache, updateEntryInCache, removeEntryFromCache } = useTimeEntriesCache();

  const [runningEntry, setRunningEntry] = useState<TimeEntry | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load running entry
  const loadRunningEntry = useCallback(async () => {
    try {
      const response = await timeTrackingService.getRunningEntry();
      setRunningEntry(response.runningEntry);
    } catch (err) {
      console.error("Error loading running entry:", err);
      // Don't set error for running entry as it's optional
      // Just log the error for debugging
    }
  }, []);

  // Start a new time entry
  const startTimeEntry = useCallback(
    async (input: CreateTimeEntryInput) => {
      try {
        setLoading(true);
        setError(null);
        const response = await timeTrackingService.startTimeEntry(input);
        setRunningEntry(response.timeEntry);

        // Optimistically add to cache
        addEntryToCache(response.timeEntry);

        return response;
      } catch (err: unknown) {
        console.error("Error starting time entry:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to start time entry";
        setError(errorMessage);
        setRunningEntry(null); // Always clear on error
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [addEntryToCache]
  );

  // Create a completed time entry (manual entry)
  const createTimeEntry = useCallback(
    async (input: CreateTimeEntryInput & { endTime: string }) => {
      try {
        setLoading(true);
        setError(null);
        const response = await timeTrackingService.createTimeEntry(input);

        // Optimistically add to cache
        addEntryToCache(response.timeEntry);

        return response;
      } catch (err: unknown) {
        console.error("Error creating time entry:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create time entry";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [addEntryToCache]
  );

  // Stop the running time entry
  const stopTimeEntry = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        setError(null);
        const response = await timeTrackingService.stopTimeEntry(id);
        setRunningEntry(null);

        // Optimistically update in cache (stopped entry has endTime now)
        updateEntryInCache(id, response.timeEntry);

        return response;
      } catch (err: unknown) {
        console.error("Error stopping time entry:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to stop time entry";
        setError(errorMessage);
        setRunningEntry(null); // Always clear on error
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [updateEntryInCache]
  );

  // Update a time entry
  const updateTimeEntry = useCallback(
    async (id: string, updates: UpdateTimeEntryInput) => {
      try {
        setLoading(true);
        setError(null);
        const response = await timeTrackingService.updateTimeEntry(id, updates);

        // Optimistically update in cache
        updateEntryInCache(id, response.timeEntry);

        // Update running entry if it's the one being updated
        if (runningEntry?.id === id) {
          setRunningEntry(response.timeEntry);
        }

        return response;
      } catch (err: unknown) {
        console.error("Error updating time entry:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update time entry";
        setError(errorMessage);
        setRunningEntry(null); // Always clear on error
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [runningEntry, updateEntryInCache]
  );

  // Delete a time entry
  const deleteTimeEntry = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        setError(null);
        await timeTrackingService.deleteTimeEntry(id);

        // Optimistically remove from cache
        removeEntryFromCache(id);

        // Clear running entry if it's the one being deleted
        if (runningEntry?.id === id) {
          setRunningEntry(null);
        }
      } catch (err: unknown) {
        console.error("Error deleting time entry:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete time entry";
        setError(errorMessage);
        setRunningEntry(null); // Always clear on error
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [runningEntry, removeEntryFromCache]
  );

  // Resume a time entry (start new entry with same properties)
  const resumeTimeEntry = useCallback(
    async (entry: TimeEntry, preferredTimezone?: string | null) => {
      try {
        setLoading(true);
        setError(null);

        // Create input with the same properties as the original entry
        // Use user's preferred timezone (or browser default if not set)
        const effectiveTimezone = getEffectiveTimezone(preferredTimezone);
        const input: CreateTimeEntryInput = {
          description: entry.description,
          projectId: entry.projectId,
          location: entry.location,
          tags: entry.tags,
          timezone: effectiveTimezone,
        };

        const response = await timeTrackingService.startTimeEntry(input);
        setRunningEntry(response.timeEntry);

        // Optimistically add to cache
        addEntryToCache(response.timeEntry);

        return response;
      } catch (err: unknown) {
        console.error("Error resuming time entry:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to resume time entry";
        setError(errorMessage);
        setRunningEntry(null); // Always clear on error
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [addEntryToCache]
  );

  // Get current duration for running entry (with live updates)
  const getCurrentDuration = useCallback(
    (entry: TimeEntry) => {
      if (entry.endTime) {
        return (
          entry.duration ||
          timeTrackingService.calculateDuration(entry.startTime, entry.endTime)
        );
      } else {
        // Use refreshTrigger to force recalculation on each render
        void refreshTrigger;
        return timeTrackingService.calculateDuration(entry.startTime);
      }
    },
    [refreshTrigger]
  );

  // Get current duration for completed entries only (stable, no refreshTrigger dependency)
  const getStableDuration = useCallback(
    (entry: TimeEntry) => {
      if (entry.endTime) {
        return (
          entry.duration ||
          timeTrackingService.calculateDuration(entry.startTime, entry.endTime)
        );
      } else {
        // For running entries, return 0 or current duration without refreshTrigger
        return timeTrackingService.calculateDuration(entry.startTime);
      }
    },
    [] // No dependencies, so this function reference never changes
  );

  // Format duration
  const formatDuration = useCallback((seconds: number) => {
    return timeTrackingService.formatDuration(seconds);
  }, []);

  // Auto-refresh for running entries
  useEffect(() => {
    // Check if there's a running entry instead of scanning the entire timeEntries array
    if (runningEntry) {
      const interval = setInterval(() => {
        // Force re-render by updating trigger
        setRefreshTrigger((prev) => prev + 1);
      }, 1000); // Update every second

      return () => clearInterval(interval);
    }
  }, [runningEntry]);

  // Load data on mount only when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadRunningEntry();
    }
  }, [loadRunningEntry, isAuthenticated]);

  const contextValue = useMemo(
    () => ({
      runningEntry,
      loading,
      error,
      startTimeEntry,
      stopTimeEntry,
      updateTimeEntry,
      deleteTimeEntry,
      loadRunningEntry,
      getCurrentDuration: getStableDuration, // Use stable version for context
      formatDuration,
      createTimeEntry,
      resumeTimeEntry,
      // Also export the live-updating version for components that need it
      getLiveDuration: getCurrentDuration,
    }),
    [
      runningEntry,
      loading,
      error,
      startTimeEntry,
      stopTimeEntry,
      updateTimeEntry,
      deleteTimeEntry,
      loadRunningEntry,
      getStableDuration, // This function reference never changes
      formatDuration,
      createTimeEntry,
      resumeTimeEntry,
      getCurrentDuration, // Add the missing dependency
    ]
  );

  return contextValue;
}

export function useTimeTrackingReport(
  dateRange: { startDate: string; endDate: string },
  projectId?: string,
  location?: string,
  sort?: string,
  isAuthenticated: boolean = false
) {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchParams, setLastFetchParams] = useState<string>("");

  // Load time entries for reporting
  const loadTimeEntriesForReport = useCallback(
    async (
      startDate: string,
      endDate: string,
      projectId?: string,
      location?: string,
      sort?: string
    ) => {
      try {
        setLoading(true);
        setError(null);

        const response = await timeTrackingService.getTimeEntriesForReport(
          startDate,
          endDate,
          projectId,
          location,
          sort
        );

        setTimeEntries(response.timeEntries);
      } catch (err) {
        console.error("Error loading time entries for report:", err);
        // Check if it's an authentication error
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          setError("Authentication required. Please log in again.");
        } else {
          setError("Failed to load time entries for report");
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Auto-fetch when parameters change
  useEffect(() => {
    if (!isAuthenticated) return;

    // Validate date range before making API call
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    if (startDate > endDate) {
      console.warn(
        "Invalid date range: startDate > endDate, skipping API call"
      );
      return;
    }

    const params = JSON.stringify({ dateRange, projectId, location, sort });
    if (params === lastFetchParams) return; // Avoid unnecessary fetches

    setLastFetchParams(params);
    loadTimeEntriesForReport(
      dateRange.startDate,
      dateRange.endDate,
      projectId,
      location,
      sort
    );
  }, [
    dateRange,
    projectId,
    location,
    sort,
    isAuthenticated,
    loadTimeEntriesForReport,
    lastFetchParams,
  ]);

  return {
    timeEntries,
    loading,
    error,
    loadTimeEntriesForReport,
  };
}
